import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@verlo/shared/components/ui/dialog';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Textarea } from '@verlo/shared/components/ui/textarea';
import { Label } from '@verlo/shared/components/ui/label';
import { Badge } from '@verlo/shared/components/ui/badge';
import { Plus, Edit3 } from 'lucide-react';
import { OptionChips } from '@verlo/shared/components/ui/segmented';

// Pre-defined common modifiers
const commonModifiers = [
  'No Onion',
  'No Tomato',
  'No Sauce',
  'Extra Cheese',
  'Gluten-Free',
  'No Salt',
  'Well Done',
  'Medium Rare',
  'No Ice',
  'Extra Hot',
  'Dairy-Free',
  'No Nuts',
];

const ItemEditDialog = ({
  open,
  onOpenChange,
  itemName,
  modifiers,
  notes,
  allergyNotes,
  customModifier,
  onModifiersChange,
  onNotesChange,
  onAllergyNotesChange,
  onCustomModifierChange,
  onSave
}) => {
  const toggleModifier = (mod) => {
    if (modifiers.includes(mod)) {
      onModifiersChange(modifiers.filter(m => m !== mod));
    } else {
      onModifiersChange([...modifiers, mod]);
    }
  };

  const addCustomModifier = () => {
    if (customModifier.trim() && !modifiers.includes(customModifier.trim())) {
      onModifiersChange([...modifiers, customModifier.trim()]);
      onCustomModifierChange('');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="form">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit3 size={18} />
            Edit Item: {itemName}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {/* Pre-defined Modifiers */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Quick Modifiers</Label>
            <OptionChips
              multiple
              value={modifiers}
              onChange={(next) => {
                // toggleModifier handles one at a time; find the one that changed.
                const changed = next.length > modifiers.length ? next.find((m) => !modifiers.includes(m)) : modifiers.find((m) => !next.includes(m));
                if (changed) toggleModifier(changed);
              }}
              options={commonModifiers.map((mod) => ({ value: mod, label: mod, testId: `modifier-${mod.toLowerCase().replace(/\s/g, '-')}` }))}
            />
          </div>

          {/* Custom Modifier Input */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Add Custom Modifier</Label>
            <div className="flex gap-2">
              <Input
                placeholder="e.g., Extra crispy"
                value={customModifier}
                onChange={(e) => onCustomModifierChange(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addCustomModifier()}
                data-testid="custom-modifier-input"
              />
              <Button 
                variant="outline" 
                onClick={addCustomModifier}
                disabled={!customModifier.trim()}
              >
                <Plus size={16} />
              </Button>
            </div>
          </div>

          {/* Selected Modifiers Display */}
          {modifiers.length > 0 && (
            <div className="space-y-2">
              <Label className="text-sm font-medium">Selected Modifiers</Label>
              <div className="flex flex-wrap gap-2">
                {modifiers.map((mod, i) => (
                  <Badge
                    key={i}
                    className="bg-verlo cursor-pointer"
                    onClick={() => onModifiersChange(modifiers.filter(m => m !== mod))}
                  >
                    {mod} ✕
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Allergy Notes */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-signal-stop">⚠️ Allergy Information</Label>
            <Textarea
              placeholder="Enter any allergy information for this item..."
              value={allergyNotes}
              onChange={(e) => onAllergyNotesChange(e.target.value)}
              className="h-16 border-signal-stop-line focus:border-signal-stop-line"
              data-testid="allergy-notes-input"
            />
          </div>

          {/* General Notes */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Special Instructions</Label>
            <Textarea
              placeholder="Any other special requests..."
              value={notes}
              onChange={(e) => onNotesChange(e.target.value)}
              className="h-16"
              data-testid="item-notes-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button className="btn-primary" onClick={onSave} data-testid="save-modifications-btn">
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ItemEditDialog;
